import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Users, Info, Calendar, Loader2, Save, Trash2, AlertTriangle, Database } from 'lucide-react';
import { toast } from 'sonner';
import { MONTHS } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SettingsPage() {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ fy_start_month: 4 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'invoices', 'clients', 'all'
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [dataCounts, setDataCounts] = useState({ invoices: 0, clients: 0 });

  useEffect(() => {
    fetchSettings();
    fetchDataCounts();
    if (isAdmin()) {
      fetchUsers();
    }
  }, []);

  const fetchDataCounts = async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/invoices`),
        axios.get(`${API_URL}/clients`)
      ]);
      setDataCounts({
        invoices: invoicesRes.data.length,
        clients: clientsRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch data counts:', error);
    }
  };

  useEffect(() => {
    fetchSettings();
    if (isAdmin()) {
      fetchUsers();
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await axios.put(`${API_URL}/settings`, {
        fy_start_month: settings.fy_start_month
      });
      toast.success(response.data.message || 'Settings saved successfully. Financial year calculations have been updated.');
      // Refresh the page data to show updated FY values
      window.dispatchEvent(new CustomEvent('fy-settings-updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const getRoleBadge = (role) => {
    const isAdminRole = role === 'admin';
    return (
      <span className={`badge ${isAdminRole ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
        {isAdminRole ? 'Admin' : 'View + Export'}
      </span>
    );
  };

  const getFYEndMonth = (startMonth) => {
    const endMonth = startMonth === 1 ? 12 : startMonth - 1;
    return MONTHS[endMonth - 1];
  };

  const openDeleteDialog = (type) => {
    setDeleteType(type);
    setConfirmText('');
    setDeleteDialogOpen(true);
  };

  const getDeleteConfirmWord = () => {
    switch(deleteType) {
      case 'invoices': return 'DELETE INVOICES';
      case 'clients': return 'DELETE CLIENTS';
      case 'all': return 'DELETE ALL';
      default: return '';
    }
  };

  const handleDelete = async () => {
    if (confirmText !== getDeleteConfirmWord()) {
      toast.error(`Please type "${getDeleteConfirmWord()}" to confirm`);
      return;
    }

    setDeleting(true);
    try {
      let endpoint = '';
      switch(deleteType) {
        case 'invoices':
          endpoint = '/admin/clear-invoices';
          break;
        case 'clients':
          endpoint = '/admin/clear-clients';
          break;
        case 'all':
          endpoint = '/admin/clear-all-data?confirm=DELETE_ALL_DATA';
          break;
        default:
          return;
      }

      const response = await axios.post(`${API_URL}${endpoint}`);
      toast.success(response.data.message || 'Data deleted successfully');
      setDeleteDialogOpen(false);
      setConfirmText('');
      fetchDataCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete data');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Manage your account and application settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current User Info */}
        <Card className="hover-lift">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Your Account</CardTitle>
                <CardDescription>Current user information</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground">Name</span>
                <span className="font-medium">{user?.name}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{user?.email}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-muted-foreground">Role</span>
                {getRoleBadge(user?.role)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Year Settings */}
        <Card className="hover-lift">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-accent/20">
                <Calendar className="h-6 w-6 text-accent-foreground" />
              </div>
              <div>
                <CardTitle>Financial Year Settings</CardTitle>
                <CardDescription>Configure your fiscal year period</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>FY Start Month</Label>
                <Select 
                  value={String(settings.fy_start_month)} 
                  onValueChange={(v) => setSettings({...settings, fy_start_month: parseInt(v)})}
                  disabled={!isAdmin()}
                >
                  <SelectTrigger data-testid="fy-start-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month, idx) => (
                      <SelectItem key={month} value={String(idx + 1)}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Current setting: {MONTHS[settings.fy_start_month - 1]} to {getFYEndMonth(settings.fy_start_month)}
                </p>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Example:</strong> If FY starts in April (default for India), then:
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  <li>• Payment in April 2024 → FY 2024-25</li>
                  <li>• Payment in March 2024 → FY 2023-24</li>
                </ul>
              </div>

              {isAdmin() && (
                <Button 
                  onClick={handleSaveSettings} 
                  disabled={savingSettings}
                  className="w-full"
                  data-testid="save-fy-settings-btn"
                >
                  {savingSettings ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save FY Settings
                </Button>
              )}

              {!isAdmin() && (
                <p className="text-xs text-amber-600">
                  Only admins can modify FY settings
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Role Permissions */}
      <Card className="hover-lift">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-secondary/10">
              <Info className="h-6 w-6 text-secondary" />
            </div>
            <div>
              <CardTitle>Role Permissions</CardTitle>
              <CardDescription>What each role can do</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-primary mb-2">Admin</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Create, edit, delete invoices</li>
                <li>• Create, edit, delete clients</li>
                <li>• Import CSV (Invoices & Clients)</li>
                <li>• Export CSV (Invoices & Clients)</li>
                <li>• View and filter all reports</li>
                <li>• Manage users</li>
                <li>• Edit notes</li>
                <li>• Configure FY settings</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-secondary mb-2">View + Export</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• View invoices and clients</li>
                <li>• View all reports</li>
                <li>• Export CSV</li>
                <li className="text-destructive">• Cannot create, edit, delete</li>
                <li className="text-destructive">• Cannot import data</li>
                <li className="text-destructive">• Cannot modify settings</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Management (Admin Only) */}
      {isAdmin() && (
        <Card className="hover-lift border-destructive/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-destructive/10">
                <Database className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <CardTitle>Data Management</CardTitle>
                <CardDescription>Manage and clear application data</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">{dataCounts.invoices}</p>
                  <p className="text-sm text-muted-foreground">Total Invoices</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">{dataCounts.clients}</p>
                  <p className="text-sm text-muted-foreground">Total Clients</p>
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Danger Zone</p>
                    <p className="text-xs text-amber-700">These actions are irreversible. Make sure to export your data before deleting.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Button 
                  variant="outline" 
                  className="border-destructive/50 text-destructive hover:bg-destructive/10"
                  onClick={() => openDeleteDialog('invoices')}
                  data-testid="delete-all-invoices-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Invoices
                </Button>
                <Button 
                  variant="outline" 
                  className="border-destructive/50 text-destructive hover:bg-destructive/10"
                  onClick={() => openDeleteDialog('clients')}
                  data-testid="delete-all-clients-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete All Clients
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => openDeleteDialog('all')}
                  data-testid="delete-all-data-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Everything
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users List (Admin Only) */}
      {isAdmin() && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-secondary/10">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <CardTitle>All Users</CardTitle>
                <CardDescription>System users with access</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{getRoleBadge(u.role)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              {deleteType === 'invoices' && `This will permanently delete all ${dataCounts.invoices} invoices.`}
              {deleteType === 'clients' && `This will permanently delete all ${dataCounts.clients} clients. Note: Delete invoices first if they reference these clients.`}
              {deleteType === 'all' && `This will permanently delete ALL data: ${dataCounts.invoices} invoices and ${dataCounts.clients} clients.`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive font-medium">
                Type <span className="font-mono bg-destructive/20 px-2 py-1 rounded">{getDeleteConfirmWord()}</span> to confirm:
              </p>
            </div>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={getDeleteConfirmWord()}
              className="font-mono"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting || confirmText !== getDeleteConfirmWord()}
              data-testid="confirm-delete-data-btn"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
